/**
 * Wire-format types for SPA → admin-service ingest.
 *
 * These mirror the (minimal subset of) the `IngestBatchDto` declared in
 * `admin-service/src/otel-ingest/dto/spa-ingest-batch.dto.ts`. When the backend
 * DTO grows, this file must be updated in lockstep.
 *
 * This file intentionally duplicates the enum string values (rather than
 * importing from `@steadman-philippon-research-institute/common-library`) so
 * the SPA bundle stays free of Node-only dependencies.
 */

/** Must match `IngestEventKind` in common-library/types/otel.ts */
export enum SpaIngestEventKind {
  PERFORMANCE = 'PERFORMANCE',
}

/** Must match `PerformanceEventType` in common-library/types/otel.ts */
export enum SpaPerformanceEventType {
  PAGE_LOAD = 'PAGE_LOAD',
}

/**
 * Schema version for the SPA ingest payload. Bump when the wire format
 * changes; admin-service rejects mismatched versions.
 */
export const SPA_INGEST_SCHEMA_VERSION = 1 as const;

/** A single PAGE_LOAD event, ready to be batched. */
export interface SpaPageLoadIngestEvent {
  id: string;
  tenantId: string;
  occurredAt: string;
  sampleRatio?: number;
  kind: SpaIngestEventKind.PERFORMANCE;
  eventType: SpaPerformanceEventType.PAGE_LOAD;
  pageName: string;
  durationSeconds: number;
}

export interface SpaIngestEmitter {
  service: string;
  environment: 'local' | 'development' | 'staging' | 'production';
}

export interface SpaIngestBatch {
  schemaVersion: typeof SPA_INGEST_SCHEMA_VERSION;
  emitter: SpaIngestEmitter;
  events: SpaPageLoadIngestEvent[];
}

/** Sentinel page name used for the one-time app bootstrap span. */
export const APP_BOOTSTRAP_PAGE_NAME = '__app:bootstrap__';

/**
 * OTEL span attribute keys recognized by `MetricsSpanProcessor`. The router
 * tracker writes these onto the span; the processor reads them back.
 */
export const SPA_OTEL_ATTR = {
  TENANT_ID: 'tenant.id',
  EVENT_TYPE: 'event.type',
  PAGE_NAME: 'page.name',
  DURATION_SECONDS: 'duration.seconds',
  SAMPLE_RATIO: 'sample.ratio',
  SOURCE_EVENT_ID: 'source.event_id',
} as const;

/** Span name prefix our processor recognizes; everything else is dropped. */
export const SPA_PERF_SPAN_PREFIX = 'metrics.performance.';
